/**
 * What WhatsApp reported about this campaign — the half of the results panel
 * that did not exist before slice 12.
 *
 * Until this slice a WhatsApp message was marked `sent` the moment the Graph API
 * accepted it and never moved again, so every screen in the product stopped at
 * "the provider took it" and said so in a sentence. Meta's status webhook now
 * writes `sent`, `delivered`, `read` and `failed` rows into `delivery_events`
 * like the SES ingest does, and this section is where those four become
 * something a person can act on.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * THE ONE RULE THIS FILE EXISTS TO ENFORCE: A READ FIGURE IS A FLOOR.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Anybody can switch read receipts off in WhatsApp. Meta then reports no `read`
 * status for that person — not a late one, not a partial one, **none, ever**,
 * however carefully they read the message. They are missing from the numerator
 * and there is no signal anywhere that says how many of them a campaign had. So
 * "38% read" does not mean 38% of people read it; it means at least 38% did, and
 * nothing in the system can say how much higher the real number is.
 *
 * A marketer who reads that figure as a readership rate will make two expensive
 * decisions with it: they will conclude a template failed when it did not, and
 * they will compare it against an email open rate that is broken in a completely
 * different way. That is why the percentage is never drawn on its own here:
 *
 *   - **the count comes first and the percentage second.** "412 of 1,204
 *     delivered messages were marked read" is true at every volume and in every
 *     read-receipt configuration. The percentage is a derived thing that needs a
 *     paragraph attached; the count needs nothing;
 *   - **the sentence is beside the number, not under the card.** Slice 11
 *     settled this for the email open rate and the reasoning carries over
 *     unchanged: a caveat a reader has to scroll to find is a caveat that was not
 *     made. Both of `WA_READ_CAVEAT_MESSAGES` are printed here, always, in full.
 *     **Slice 17 cut the hand-written paragraph above them to its verdict and
 *     left the two contract sentences untouched**, and the order matters: that
 *     paragraph had grown into a third account of the same two mechanisms —
 *     receipts off undercounts, an opened chat overcounts — sitting directly
 *     above the two sentences that say exactly that, from the contract, where
 *     every other screen reads them. Three tellings of one fact is how the
 *     shortest becomes the one people trust. What is left says the only thing
 *     the caveats do not: that the figure is not a readership rate and cannot be
 *     made into one. **The caveats themselves are not shortenable from here** —
 *     they are the contract's, `AnalyticsWhatsAppOutcomes` prints the same two,
 *     and a screen paraphrasing them is how one screen's hedge becomes weaker
 *     than another's;
 *   - **there is no colour, no threshold and no verdict badge.** All three are
 *     claims that somebody knows what a good number looks like. Nobody does:
 *     nothing is wrong at 30% and nothing is right at 60%, because neither figure
 *     is the figure. The one badge present says "Floor, not a rate", which is the
 *     opposite of a verdict;
 *   - **the count and the denominator are not over the same population, and the
 *     component says so rather than hiding it.** `read` is counted over every
 *     message in the campaign and `delivered` over the ones that reached a
 *     device, because Meta drops and reorders statuses — so a campaign can hold
 *     read receipts with no delivery figure at all, or more reads than
 *     deliveries. Neither is a bug and neither may be papered over: the reads
 *     are printed as a count, and the percentage is withheld, because "at least
 *     100%" over a denominator that is missing rows is a false claim rather than
 *     a rounded one;
 *   - **it is drawn last and smallest in the section.** Visual rank is a claim
 *     about how much a number should move a decision. The failure breakdown above
 *     it can cost a tenant their WhatsApp quality rating; this cannot.
 *
 * **The contract does most of that work and this file leans on it rather than
 * re-deciding it.** `WaReadRate` is not a number: it carries `confidence:
 * 'floor'` and a non-empty caveat list, it has no `band` and no `threshold`, and
 * it is mutually non-assignable to both `OverviewRateKpi` (the bounce-rate KPI
 * tile, which draws an AWS line under a number) and `EngagementRate` (the email
 * open rate, whose confidence is the literal `'directional'`). That last one is
 * the distinction worth keeping: an open rate is wrong in *both* directions at
 * once, so it licenses "approximate"; a read count is only ever *too low*, so it
 * licenses "at least". A component written for one of those sentences must not
 * be handed the other's number, and the compiler is what says so. Nothing here
 * unwraps a `WaReadRate` into a bare `number` and passes it on — the whole object
 * travels together with its caveats attached, because a rate separated from its
 * hedge is an exact rate as far as the reader is concerned.
 *
 * **Below `WA_MIN_VOLUME_FOR_READ_RATE` delivered messages there is no
 * percentage at all** — `reading.rate` is `null` and the counts are shown
 * instead, the same rule the Overview follows for every rate it prints. A read
 * rate over eleven delivered messages moves nine points per person.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *
 * **What is not here, and is not an oversight.** There is no click figure. Meta
 * does report button clicks, but through Template Analytics: aggregated per
 * template per day, with no recipient identity in it at all. It is the same
 * shape as the per-ISP deliverability panel on the Overview and gets the same
 * treatment when it is built — a panel that reports on a template, never a
 * number joined to a contact and never a column in this section. Nothing in this
 * file would have to change to add it, which was the point of leaving it out.
 *
 * **And there is no "bounced".** Decision 1: WhatsApp has no such category. The
 * SES-shaped bounce counters and `CampaignNotDelivered` are gated off this
 * channel by `isBounceMeasured` one file up, and what stands in their place is
 * `CampaignWaFailures` — seven classes of `failed`, kept apart because Meta
 * reports all seven with the same word.
 */
import {
  WA_READ_CAVEAT_MESSAGES,
  type WaCampaignDelivery,
  type WaReadRate,
} from '@wend/shared';
import { Badge, Kicker } from '@wend/ui';

import { formatCount, formatDateTime, pluralise } from '../../lib/format';
import { Counter } from './CampaignFailureSections';
import { CampaignWaFailures } from './CampaignWaFailures';

function formatRate(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

export interface CampaignWhatsAppDeliveryProps {
  delivery: WaCampaignDelivery;
}

export function CampaignWhatsAppDelivery({ delivery }: CampaignWhatsAppDeliveryProps) {
  return (
    <>
      {/* What did not arrive, and whether anybody can do anything about it. */}
      <div className="flex flex-col gap-3 pt-2">
        <Kicker rule>
          Failures
        </Kicker>

        <dl className="flex flex-wrap gap-2.5">
          <Counter
            label="Failed"
            value={delivery.failed}
            tone={delivery.failed > 0 ? 'text-bad' : undefined}
          />
        </dl>

        <p className="max-w-prose text-[0.85rem] leading-relaxed text-muted">
          {delivery.failed === 0
            ? `WhatsApp took ${pluralise(delivery.accepted, 'message')} from this campaign and has not reported any of them as failed. `
            : `WhatsApp took ${pluralise(delivery.accepted, 'message')} from this campaign and later reported that ${pluralise(delivery.failed, 'message')} did not arrive. `}
          A failure gives its credit back rather than consuming it, and suppresses nobody unless the
          number can never receive a WhatsApp message at all — a rate limit leaves the contact
          exactly as it was.
        </p>
      </div>

      <CampaignWaFailures breakdown={delivery.notDelivered} failed={delivery.failed} />

      {/* Read receipts — last in the section, and the reason is its rank. */}
      <WaReadFloor read={delivery.read} delivered={delivery.delivered} rate={delivery.readRate} />

      {delivery.lastStatusAt !== null && (
        <p className="font-mono text-[0.68rem] uppercase tracking-[0.1em] text-muted">
          Last status from WhatsApp {formatDateTime(delivery.lastStatusAt)}
        </p>
      )}
    </>
  );
}

/**
 * The read figure, drawn so it cannot be mistaken for a readership rate.
 *
 * Takes the whole {@link WaReadRate} rather than a number, and never takes the
 * caveats apart from the reading: a caller cannot obtain a percentage from this
 * module without the two sentences that go with it, because the two sentences
 * are rendered by the same component that renders the percentage.
 *
 * The raw `read` and `delivered` counts are passed alongside it even though the
 * reading carries a numerator and a denominator, and they are the same numbers.
 * That redundancy is deliberate: `reading.numerator` is an input to an
 * arithmetic that may have refused to produce a rate, while `delivery.read` is
 * the count this campaign actually achieved — and the count is the figure that
 * is printed either way, at every volume, so it comes from the field that means
 * it.
 */
function WaReadFloor({
  read,
  delivered,
  rate,
}: {
  read: number;
  delivered: number;
  rate: WaReadRate;
}) {
  const { reading } = rate;

  /*
   * READ AND DELIVERED ARE COUNTED OVER DIFFERENT POPULATIONS, AND THIS IS THE
   * COMPONENT THAT HAS TO KNOW IT.
   *
   * The API counts `read` over every message in the campaign, not only over the
   * delivered ones, and defends that choice in its own comment: a `read` whose
   * `delivered` never arrived is a real message a real person really read, and
   * dropping it would understate a figure that is already a floor. Two things
   * follow, and this component used to get both wrong:
   *
   *   - `delivered === 0` does **not** imply `read === 0`. Printing "nothing
   *     could have been marked read" beside twelve read receipts on file tells
   *     the reader a measurement is impossible on data that was actually
   *     measured;
   *   - `read` can exceed `delivered`. "150 of 120 delivered" is a
   *     self-contradiction, and the percentage beside it — clamped to
   *     "at least 100.0%" — is a floor claim that is not true of the population
   *     it names.
   *
   * Both cases mean the same thing and get the same sentence: Meta has not
   * reported all of the deliveries. The reads are still real, so the count is
   * still shown; it is the *ratio* that has nothing to divide by.
   */
  const nothingMeasured = delivered === 0 && read === 0;
  const denominatorIncomplete = read > delivered;

  return (
    <div className="flex flex-col gap-3 pt-2">
      <Kicker rule>
        Marked read
      </Kicker>

      {nothingMeasured ? (
        <p className="max-w-prose text-[0.92rem] leading-relaxed text-muted">
          Nothing has been delivered on this campaign yet and no read receipt has come back, so
          there is nothing to report here.
        </p>
      ) : (
        <div className="flex flex-col gap-2.5">
          {/* The count leads. It is true at every volume and under every
              read-receipt setting; the percentage below it is neither. */}
          <p className="text-[1.35rem] font-semibold leading-none tabular-nums text-text">
            {formatCount(read)}
            <span className="text-[0.9rem] text-muted">
              {denominatorIncomplete
                ? ' marked read'
                : ` of ${formatCount(delivered)} delivered`}
            </span>
          </p>

          {denominatorIncomplete ? (
            <p className="max-w-prose text-[0.85rem] leading-relaxed text-muted">
              No percentage: WhatsApp has reported{' '}
              {pluralise(delivered, 'delivery', 'deliveries')} on this campaign — fewer than the
              reads — so there is nothing complete to divide by. The reads themselves are real.
            </p>
          ) : reading.rate === null ? (
            <p className="max-w-prose text-[0.85rem] leading-relaxed text-muted">
              Not enough volume for a percentage. Under {formatCount(reading.minimumVolume)}{' '}
              delivered messages a read figure moves several points per person.
            </p>
          ) : (
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              {/* No band colour and no threshold line, by design — see the file
                  header. A verdict needs somebody who can say what a good
                  number is, and on this figure nobody can. */}
              <span className="text-[1.5rem] font-semibold leading-none tabular-nums text-text">
                at least {formatRate(reading.rate)}
              </span>
              <Badge tone="neutral">Floor, not a rate</Badge>
            </div>
          )}

          {/* THE SENTENCE, AT THE NUMBER. Not a footnote, not a tooltip. */}
          <p className="max-w-prose text-[0.85rem] leading-relaxed text-muted">
            This is not the share of people who read this campaign, and it cannot be turned into
            one. Both directions it is wrong in are below.
          </p>

          <ul className="flex flex-col gap-1.5">
            {rate.caveats.map((caveat) => (
              <li key={caveat} className="text-[0.78rem] leading-relaxed text-muted">
                {WA_READ_CAVEAT_MESSAGES[caveat]}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
